import { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Report } from '../../models/report';

const getDefaultStartMonth = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 11);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const getDefaultEndMonth = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonth = (monthStr) => {
  const [year, month] = monthStr.split('-');
  return `${month}/${year}`;
};

const getChurnColor = (rate) => {
  if (rate <= 5) return 'success';
  if (rate <= 15) return 'warning';
  return 'error';
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{label}</Typography>
      {payload.map((entry) => (
        <Typography key={entry.dataKey} variant="body2" sx={{ color: entry.color }}>
          {entry.name}: {entry.dataKey === 'churn_rate' ? `${entry.value}%` : entry.value}
        </Typography>
      ))}
    </Paper>
  );
};

const MonthlyChurnReport = ({ setPageTitle }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [startMonth, setStartMonth] = useState(getDefaultStartMonth());
  const [endMonth, setEndMonth] = useState(getDefaultEndMonth());

  useEffect(() => {
    setPageTitle('Thống kê churn gói dịch vụ');
  }, [setPageTitle]);

  useEffect(() => {
    const fetchReport = async () => {
      if (!startMonth || !endMonth) {
        setData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await Report.fetch('monthly_churn', {
          start_month: startMonth,
          end_month: endMonth,
        });

        const chartData = response.map((item) => ({
          ...item,
          label: formatMonth(item.month),
        }));

        setData(chartData);
      } catch (error) {
        console.error('Error fetching monthly churn report:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [startMonth, endMonth]);

  return (
    <Box sx={{ maxWidth: 1200, margin: '24px auto', pl: 3, pr: 3 }}>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          label="Tháng bắt đầu"
          type="month"
          value={startMonth}
          onChange={(e) => setStartMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Tháng kết thúc"
          type="month"
          value={endMonth}
          onChange={(e) => setEndMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {loading ? (
        <Skeleton variant="rectangular" width="100%" height={400} />
      ) : data.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Không tìm thấy dữ liệu cho khoảng thời gian đã chọn
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Chart */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Biểu đồ churn hàng tháng
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" fontSize={12} />
                <YAxis
                  yAxisId="left"
                  allowDecimals={false}
                  label={{ value: 'Số lượng', angle: -90, position: 'insideLeft' }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  label={{ value: 'Tỷ lệ churn', angle: 90, position: 'insideRight' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="active_count"
                  name="Đang hoạt động"
                  fill="#4caf50"
                  radius={[2, 2, 0, 0]}
                />
                <Bar
                  yAxisId="left"
                  dataKey="not_extended_count"
                  name="Không gia hạn"
                  fill="#f44336"
                  radius={[2, 2, 0, 0]}
                />
                <Bar
                  yAxisId="left"
                  dataKey="new_users_count"
                  name="Khách mới"
                  fill="#2196f3"
                  radius={[2, 2, 0, 0]}
                />
                <Bar
                  yAxisId="left"
                  dataKey="return_users_count"
                  name="Khách quay lại"
                  fill="#ff9800"
                  radius={[2, 2, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="churn_rate"
                  name="Tỷ lệ churn (%)"
                  stroke="#9c27b0"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>

          {/* Data Table */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Chi tiết theo tháng
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tháng</TableCell>
                    <TableCell align="right">Đang hoạt động</TableCell>
                    <TableCell align="right">Không gia hạn</TableCell>
                    <TableCell align="right">Khách mới</TableCell>
                    <TableCell align="right">Khách quay lại</TableCell>
                    <TableCell align="right">Tỷ lệ churn</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row) => (
                    <TableRow key={row.month} hover>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right">{row.active_count}</TableCell>
                      <TableCell align="right">{row.not_extended_count}</TableCell>
                      <TableCell align="right">{row.new_users_count}</TableCell>
                      <TableCell align="right">{row.return_users_count}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${row.churn_rate}%`}
                          color={getChurnColor(row.churn_rate)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MonthlyChurnReport;
